// Кооператив (ux/07): вкладка без кооператива, поиск, карточка, создание (E07-T06); главная, участники, заявки,
// настройки, выход и голосование о роспуске (E07-T07); стройка, сдача и склад (E07-T08); лавка (E07-T12);
// «Наряды дня» Доски (E07-T16). Кнопки, на которые у роли нет прав, не показываются (ux/07 §0).
// Настройки, «Мои заявки» с отзывом, вклад и время захода участников — по Р-87; чат и группа — screens/chat.tsx.
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { I } from '../assets/icons.js';
import { Icon } from '../assets/ui.js';
import { isBlocked } from '../content/word-filter.js';
import { amount, duration, num } from '../i18n/format.js';
import { hasKey, t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import {
  coopBrief, isLeader, myCoop, myRequests, nameProblem, rejoinAt, REQUESTS_MAX, setMyCoop, setRejoinAt, spareAmount,
  type Coop, type CoopCard, type CoopRole, type MyRequest,
} from '../state/coop.js';
import { clientConfig, online, player, setWallet, tutorialStep, wallet } from '../state/game.js';
import type { ScreenProps, SheetProps } from '../ui/App.js';
import { ErrorBanner, ScreenTitle, Sheet, Skeleton, useActionBar, useNow } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { flyTo } from '../ui/fx.js';
import { useLoad } from '../ui/use-load.js';
import { GAME_LINK } from './system.js';
import { setTut, TEXT as TUT, tutT09Done } from '../tutorial/flow.js';

type App = ReturnType<typeof useApp>;
type Board = ResponseOf<Api['getCoopBoard']>;
type Shop = ResponseOf<Api['getCoopShop']>;

/** Свой кооператив — с 10-го уровня (ГД 07 §2). */
export const CREATE_MIN_LEVEL = 10;
/** Наряды (ГД 07 §6.3): виды одинаковы по механике, ID — конфиг сервера `coop.chores`. */
export const CHORES = ['build_shift', 'clear_rubble', 'gate_duty'] as const;
export const CHORE_ENERGY = 5;
/** Неделя «Собрания»: пн 04:00 — вс 12:00 по Москве — роспуск запрещён (ux/07 §14). */
export function assemblyWeek(nowMs: number): boolean {
  const msk = new Date(nowMs + 3 * 3_600_000);
  const day = msk.getUTCDay(); // 0 — воскресенье
  const h = msk.getUTCHours();
  if (day === 1) return h >= 4;
  if (day === 0) return h < 12;
  return true;
}

const ROLE_KEY: Record<CoopRole, string> = { chairman: 'COOP-ROLE-CHAIR', deputy: 'COOP-ROLE-DEPUTY', member: 'COOP-ROLE-MEMBER' };
const JOIN_KEY: Record<CoopCard['joinMode'], string> = { open: 'COOP-OPEN', request: 'COOP-BY-REQUEST', closed: 'CL-COOP-INVITE-ONLY' };
const ROLE_ORDER: Record<CoopRole, number> = { chairman: 0, deputy: 1, member: 2 };
export const BUILDING_KEY = (id: string) => `CL-COOP-BLD-${id.replace('c_', '').toUpperCase()}`;
const CHORE_KEY = (id: string) => `CL-COOP-CHORE-${id.toUpperCase().replace(/_/g, '-')}`;

const errCode = (e: unknown) => (e instanceof ApiCallError ? e.code : null);

/** Ключ идемпотентности действия (tech/03 §1): тот же при повторе после сбоя, новый — после успеха. */
function useIdem(app: App): { key: () => string; next: () => void } {
  const k = useRef(app.idem());
  return { key: () => k.current, next: () => { k.current = app.idem(); } };
}

/** Отозвать заявку (Р-87): список обновляется сразу, NOT_FOUND — заявки уже нет (приняли, отклонили, истекла). */
async function withdraw(app: App, r: MyRequest): Promise<void> {
  try {
    await app.call('withdrawCoopRequest', { params: { coopId: r.coop.coopId, reqId: r.reqId } });
    showToast(tr('CL-COOP-WITHDRAWN'));
  } catch (e) {
    if (errCode(e) !== 'NOT_FOUND') { app.handleError(e); return; }
  }
  myRequests.value = myRequests.value.filter((x) => x.reqId !== r.reqId);
}

async function reloadMine(app: App): Promise<Coop | null> {
  const b = coopBrief.value;
  if (!b) return null;
  try {
    const c = await app.call('getCoop', { params: { coopId: b.coopId } });
    if (!c.myRole) { setMyCoop(null); return null; }
    setMyCoop(c);
    return c;
  } catch (e) {
    if (errCode(e) === 'NOT_FOUND' || errCode(e) === 'NOT_IN_COOP') { setMyCoop(null); return null; }
    throw e;
  }
}

function Emblem(p: { size?: number }) {
  const s = p.size ?? 48;
  return <span class="coop-emblem" style={{ width: s, height: s }} aria-hidden><Icon id="ICO-NAV-COOP" fallback="🏘" size={Math.round(s * 0.6)} /></span>;
}

function CoopRow(p: { c: CoopCard; onClick: () => void }) {
  const full = p.c.membersCount >= p.c.capacity;
  return (
    <button class="list-row coop-row" data-testid={`coop-${p.c.coopId}`} onClick={p.onClick}>
      <Emblem />
      <span class="col" style={{ flex: 1, gap: 2, minWidth: 0 }}>
        <b class="ellipsis">{p.c.name}{p.c.tag ? <span class="muted small"> [{p.c.tag}]</span> : null}</b>
        <span class="small muted">
          {tr('CL-COOP-LVL', { n: p.c.level })} · {full ? tr('COOP-FULL', { n: p.c.capacity }) : `${p.c.membersCount} / ${p.c.capacity}`} · {tr(JOIN_KEY[p.c.joinMode])}
          {p.c.minLevel > 1 ? ` · ${tr('CL-COOP-MIN-LVL', { n: p.c.minLevel })}` : ''}
        </span>
      </span>
      {p.c.activity === 'dormant' ? <span class="badge soft">{tr('CL-COOP-DORMANT')}</span> : null}
      {p.c.npcChairman ? <span class="badge soft">{tr('CL-COOP-NPC')}</span> : null}
    </button>
  );
}

/** SCR-COOP: вкладка — главная кооператива или экран «без кооператива». */
export function CoopTabScreen(p: ScreenProps) {
  const app = useApp();
  // T09 (ux/11 §1.3): уже в кооперативе (вступил по ссылке) — шаг закрыт как «вступил».
  useEffect(() => { if (tutorialStep.value === 'T09' && coopBrief.value) void tutT09Done(app, true); }, []);
  return coopBrief.value ? <CoopHomeScreen {...p} /> : <CoopNoneScreen {...p} />;
}

// ---------- §1 SCR-COOP-NONE ----------
function CoopNoneScreen(_p: ScreenProps) {
  const app = useApp();
  const now = useNow(30_000);
  const lvl = player.value?.level ?? 1;
  const rec = useLoad(() => app.call('listCoops', { query: { recommend: '1', hasSlots: '1', limit: 5 } }));
  const inv = useLoad(() => app.call('getMyCoopInvites'));
  useLoad(() => app.call('getMyCoopRequests'), { onData: (d) => { myRequests.value = d.items; } });
  const reqs = myRequests.value;
  const cooldown = rejoinAt() - now;
  useActionBar({
    secondary: { text: tr('COOP-BTN-FIND'), tone: 'outline', testId: 'coop-find', onClick: () => app.nav.push('SCR-COOP-SEARCH') },
    main: lvl >= CREATE_MIN_LEVEL
      ? { text: tr('COOP-BTN-CREATE'), testId: 'coop-create', disabled: !online.value, onClick: () => app.nav.push('SCR-COOP-CREATE') }
      : { text: tr('CL-COOP-CREATE-LOCKED', { n: CREATE_MIN_LEVEL }), testId: 'coop-create', disabled: true, onClick: () => {} },
  });
  return (
    <>
      <ScreenTitle>{tr('UI-NAV-COOP')}</ScreenTitle>
      <div class="content" data-testid="SCR-COOP-NONE">
        <div class="page col coop-page">
          <div class="coop-plate col">
            <b class="coop-hero">{tr('CL-COOP-NONE-TITLE')}</b>
            <span>{tr('COOP-NONE')}</span>
            <ul class="coop-why">
              <li>{tr('CL-COOP-WHY-1')}</li><li>{tr('CL-COOP-WHY-2')}</li><li>{tr('CL-COOP-WHY-3')}</li>
            </ul>
          </div>
          {cooldown > 0 ? <div class="banner" data-testid="coop-cooldown">{tr('CL-COOP-COOLDOWN', { time: duration(cooldown) })}</div> : null}
          {inv.data?.items.length ? (
            <div class="coop-plate col" data-testid="coop-invites">
              <b>{tr('CL-COOP-INVITES')}</b>
              {inv.data.items.map((i) => <CoopRow key={i.inviteId} c={i.coop} onClick={() => app.nav.openSheet('MOD-COOP-PREVIEW', { coopId: i.coop.coopId, inviteId: i.inviteId, invitedBy: i.invitedBy.displayName })} />)}
            </div>
          ) : null}
          {reqs.length ? (
            <div class="coop-plate col" data-testid="coop-my-requests">
              <b>{tr('CL-COOP-MY-REQUESTS')}</b>
              {reqs.map((r) => (
                <div key={r.reqId} class="list-row" data-testid={`my-req-${r.reqId}`}>
                  <button style={{ flex: 1, textAlign: 'left' }} onClick={() => app.nav.openSheet('MOD-COOP-PREVIEW', { coopId: r.coop.coopId })}>{tr('CL-COOP-REQ-WAIT', { name: r.coop.name, time: duration(Math.max(0, Date.parse(r.expiresAt) - now)) })}</button>
                  <button class="chip" data-testid={`my-req-cancel-${r.reqId}`} disabled={!online.value} onClick={() => { void withdraw(app, r); }}>{tr('CL-COOP-WITHDRAW')}</button>
                </div>
              ))}
            </div>
          ) : null}
          <div class="coop-plate col" data-testid="coop-recommended" data-tut="tut-coop-list">
            <b>{tr('CL-COOP-RECOMMENDED')}</b>
            {rec.error && !rec.data ? <ErrorBanner what={tr('CL-COOP-WHAT')} onRetry={rec.reload} /> : null}
            {!rec.data && rec.loading ? <Skeleton h={64} n={3} /> : null}
            {rec.data && !rec.data.items.length ? <span class="muted small">{tr('CL-COOP-NO-RECOMMEND')}</span> : null}
            {rec.data?.items.map((c) => <CoopRow key={c.coopId} c={c} onClick={() => app.nav.openSheet('MOD-COOP-PREVIEW', { coopId: c.coopId })} />)}
            {tutorialStep.value === 'T09' ? <button class="btn outline" data-testid="tut-coop-later" onClick={() => tutT09Done(app, false)}>{TUT.T09later}</button> : null}
          </div>
        </div>
      </div>
    </>
  );
}

// ---------- §2 SCR-COOP-SEARCH ----------
type Filter = 'open' | 'slots' | 'level';
export function CoopSearchScreen(_p: ScreenProps) {
  const app = useApp();
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState<Filter[]>([]);
  const [items, setItems] = useState<CoopCard[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  const seq = useRef(0);
  const lvl = player.value?.level ?? 1;
  const run = async (more = false) => {
    const my = ++seq.current;
    setErr(false);
    try {
      const r = await app.call('listCoops', { query: {
        ...(q.trim().length >= 2 ? { q: q.trim() } : {}),
        ...(filters.includes('open') ? { joinMode: 'open' as const } : {}),
        ...(filters.includes('slots') ? { hasSlots: '1' as const } : {}),
        ...(filters.includes('level') ? { minLevelLe: lvl } : {}),
        ...(more && cursor ? { cursor } : {}), limit: 20,
      } });
      if (my !== seq.current) return;
      setItems(more ? [...(items ?? []), ...r.items] : r.items);
      setCursor(r.nextCursor ?? null);
    } catch (e) {
      if (my !== seq.current) return;
      setErr(true);
      showToast(tr('CL-COOP-SEARCH-FAIL'), 'error');
      void e;
    }
  };
  // Поиск — от 2 символов через 300 мс после ввода (ux/07 §2); пустое поле — общий список.
  useEffect(() => {
    if (q.trim().length === 1) return;
    const t = setTimeout(() => { void run(); }, 300);
    return () => clearTimeout(t);
  }, [q, filters.join(',')]);
  useActionBar({ back: true });
  const toggle = (f: Filter) => setFilters(filters.includes(f) ? filters.filter((x) => x !== f) : [...filters, f]);
  // Засыпающие — ниже активных (ux/07 §2).
  const sorted = items ? [...items].sort((a, b) => Number(a.activity === 'dormant') - Number(b.activity === 'dormant')) : null;
  return (
    <>
      <ScreenTitle>{tr('COOP-BTN-FIND')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-COOP-SEARCH">
        <div class="page col">
          <input class="input" type="search" data-testid="coop-q" placeholder={tr('CL-COOP-SEARCH-PH')} maxLength={24} value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
          <div class="chips">
            {(['open', 'slots', 'level'] as Filter[]).map((f) => <button key={f} class={`chip ${filters.includes(f) ? 'on' : ''}`} data-testid={`coop-f-${f}`} aria-pressed={filters.includes(f)} onClick={() => toggle(f)}>{tr(`CL-COOP-F-${f.toUpperCase()}`)}</button>)}
          </div>
          {err && !items ? <ErrorBanner what={tr('CL-COOP-WHAT')} onRetry={() => { void run(); }} /> : null}
          {!sorted && !err ? <Skeleton h={64} n={4} /> : null}
          {sorted && !sorted.length ? (
            <div class="empty" data-testid="coop-search-empty">{tr('CL-COOP-NOTHING')}{lvl >= CREATE_MIN_LEVEL ? <button class="btn accent" onClick={() => app.nav.replace('SCR-COOP-CREATE')}>{tr('COOP-BTN-CREATE')}</button> : null}</div>
          ) : null}
          <div class="list col">{sorted?.map((c) => <CoopRow key={c.coopId} c={c} onClick={() => app.nav.openSheet('MOD-COOP-PREVIEW', { coopId: c.coopId })} />)}</div>
          {cursor ? <button class="btn outline" data-testid="coop-more" onClick={() => { void run(true); }}>{tr('CL-COOP-MORE')}</button> : null}
        </div>
      </div>
    </>
  );
}

// ---------- §3 MOD-COOP-PREVIEW ----------
export function CoopPreviewSheet({ entry }: SheetProps) {
  const app = useApp();
  const coopId = String(entry.params['coopId']);
  const inviteId = entry.params['inviteId'] as string | undefined;
  const load = useLoad(() => app.call('getCoop', { params: { coopId } }));
  const [msg, setMsg] = useState('');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const now = useNow(30_000);
  const c = load.data;
  const lvl = player.value?.level ?? 1;
  const requested = myRequests.value.find((r) => r.coop.coopId === coopId);
  const cooldown = rejoinAt() - now;
  const mine = coopBrief.value;
  const tutT09 = tutorialStep.value === 'T09';
  useEffect(() => { if (tutT09) setTut({ target: 'tut-coop-preview', say: null, hint: null }); }, [tutT09]);
  const enter = async () => {
    const fresh = await app.call('getCoop', { params: { coopId } });
    setMyCoop(fresh);
    app.haptic('success');
    app.nav.closeSheets();
    app.nav.selectTab('coop');
    if (tutT09) await tutT09Done(app, true);
  };
  const join = async (message?: string) => {
    setBusy(true);
    try {
      const r = await app.call('joinCoop', { params: { coopId }, body: message ? { message } : {} });
      if (r.status === 'joined') { await enter(); return; }
      void app.call('getMyCoopRequests').then((d) => { myRequests.value = d.items; }).catch(() => {});
      showToast(tr('COOP-REQUEST-SENT'), 'success');
      if (tutT09) { await tutT09Done(app, false); return; }
      setAsking(false);
      load.reload();
    } catch (e) {
      const code = errCode(e);
      if (code === 'COOP_FULL') { showToast(tr('CL-COOP-FULL-NOW')); load.reload(); }
      else if (code === 'COOP_CLOSED') { showToast(tr('CL-COOP-NOW-REQUEST')); load.reload(); }
      else if (code === 'REJOIN_COOLDOWN') { const u = (e as ApiCallError).details?.['until']; if (typeof u === 'string') setRejoinAt(Date.parse(u)); load.reload(); }
      else app.handleError(e);
    } finally { setBusy(false); }
  };
  const decideInvite = async (accept: boolean) => {
    if (!inviteId) return;
    setBusy(true);
    try {
      await app.call('decideInvite', { params: { inviteId }, body: { accept } });
      if (accept) await enter(); else app.nav.popSheet();
    } catch (e) { app.handleError(e); } finally { setBusy(false); }
  };
  // Кнопки по ситуации (ux/07 §3): одна главная, при необходимости вторая.
  let actions: { text: string; onClick: () => unknown; disabled?: boolean; tone?: 'outline' | 'danger'; testId?: string }[] = [];
  if (c) {
    const full = c.membersCount >= c.capacity;
    const off = !online.value || busy;
    if (c.myRole) actions = [{ text: tr('CL-COOP-OPEN'), onClick: () => { app.nav.closeSheets(); app.nav.selectTab('coop'); } }];
    else if (inviteId) actions = [{ text: tr('CL-COOP-ACCEPT-INVITE'), testId: 'preview-accept', disabled: off, onClick: () => decideInvite(true) }, { text: tr('CL-COOP-DECLINE'), tone: 'outline', disabled: off, onClick: () => decideInvite(false) }];
    else if (mine) actions = [{ text: tr('CL-COOP-MOVE-HERE'), testId: 'preview-move', onClick: () => app.nav.openSheet('MOD-COOP-LEAVE', { move: c.name }) }];
    else if (cooldown > 0) actions = [{ text: tr('CL-COOP-JOIN-IN', { time: duration(cooldown) }), disabled: true, onClick: () => {} }];
    else if (lvl < c.minLevel) actions = [{ text: tr('CL-COOP-NEED-LVL', { n: c.minLevel }), disabled: true, onClick: () => {} }];
    else if (full) actions = [{ text: tr('CL-COOP-NO-SLOTS'), disabled: true, onClick: () => {} }];
    else if (c.joinMode === 'closed') actions = [{ text: tr('CL-COOP-INVITE-ONLY'), disabled: true, onClick: () => {} }];
    else if (requested) actions = [{ text: tr('CL-COOP-REQ-SENT-BTN'), disabled: true, onClick: () => {} }, { text: tr('CL-COOP-WITHDRAW-REQ'), tone: 'outline', testId: 'preview-withdraw', disabled: off, onClick: () => withdraw(app, requested) }];
    else if (c.joinMode === 'request' && myRequests.value.length >= REQUESTS_MAX) actions = [{ text: tr('CL-COOP-REQ-LIMIT'), disabled: true, onClick: () => {} }];
    else if (c.joinMode === 'request') {
      actions = asking
        ? [{ text: tr('CL-COOP-SEND'), testId: 'preview-send', disabled: off || isBlocked(msg), onClick: () => join(msg.trim() || undefined) }]
        : [{ text: tr('COOP-BTN-REQUEST'), testId: 'preview-request', disabled: off, onClick: () => setAsking(true) }];
    } else actions = [{ text: tr('COOP-BTN-JOIN'), testId: 'preview-join', disabled: off, onClick: () => join() }];
  }
  const leaders = c?.members.filter((m) => m.role !== 'member').sort((a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role]) ?? [];
  // T09: карточка-приглашение (TUT-T09-03/04) и две кнопки внутри выреза — «Вступить» и «Позже».
  if (tutT09 && c && !c.myRole) {
    const chair = c.members.find((m) => m.role === 'chairman')?.displayName ?? '';
    const join1 = c.joinMode === 'open' ? () => join() : () => join(undefined);
    return (
      <Sheet entry={entry} title={tr('TUT-T09-03', { coop: c.name })} testId="MOD-COOP-PREVIEW">
        <div class="col" data-tut="tut-coop-preview" data-testid="MOD-COOP-PREVIEW-body">
          <span>{tr('TUT-T09-04', { chair, n: c.membersCount })}</span>
          <button class="btn accent" data-testid="preview-join" disabled={busy || !online.value || c.membersCount >= c.capacity || c.joinMode === 'closed'} onClick={join1}>{TUT.T09join}</button>
          <button class="btn outline" data-testid="tut-coop-later" disabled={busy} onClick={() => tutT09Done(app, false)}>{TUT.T09later}</button>
        </div>
      </Sheet>
    );
  }
  return (
    <Sheet entry={entry} title={c?.name ?? '…'} actions={actions} testId="MOD-COOP-PREVIEW">
      {load.error === 'NOT_FOUND' ? <div class="empty" data-testid="coop-gone">{tr('CL-COOP-GONE')}</div> : !c ? <Skeleton h={56} n={3} /> : (
        <div class="col" data-testid="MOD-COOP-PREVIEW-body">
          <div class="row" style={{ gap: 12 }}>
            <Emblem size={72} />
            <div class="col" style={{ gap: 2 }}>
              <span>{tr('CL-COOP-LVL', { n: c.level })} · {tr('COOP-MEMBERS', { n: c.membersCount, max: c.capacity })}</span>
              <span class="small muted">{tr(JOIN_KEY[c.joinMode])}{c.minLevel > 1 ? ` · ${tr('CL-COOP-MIN-LVL', { n: c.minLevel })}` : ''}</span>
              {c.activity === 'dormant' ? <span class="badge soft">{tr('CL-COOP-DORMANT')}</span> : null}
            </div>
          </div>
          {entry.params['invitedBy'] ? <div class="banner">{tr('CL-COOP-INVITED-BY', { name: String(entry.params['invitedBy']) })}</div> : null}
          {c.description ? <p style={{ margin: 0 }}>{c.description.slice(0, 120)}</p> : null}
          {c.npcChairman ? <div class="banner">{tr('CL-COOP-NPC-NOTE')}</div> : null}
          {leaders.length ? (
            <div class="col" style={{ gap: 4 }}>
              {leaders.map((m) => <button key={m.publicId} class="list-row" onClick={() => app.nav.openSheet('MOD-PLAYER-CARD', { publicId: m.publicId })}><span style={{ flex: 1 }}>{m.displayName}</span><span class="small muted">{tr(ROLE_KEY[m.role])}</span></button>)}
            </div>
          ) : null}
          <span class="small muted">{tr('CL-COOP-ACTIVE-7D', { n: c.active7d })}{c.tgGroupUrl ? ` · ${tr('CL-COOP-HAS-TG')}` : ''}</span>
          {asking ? (
            <label class="col" style={{ gap: 4 }}>
              <span class="small">{tr('CL-COOP-REQ-MSG')}</span>
              <textarea class="input" data-testid="preview-msg" maxLength={120} rows={2} value={msg} onInput={(e) => setMsg((e.target as HTMLTextAreaElement).value)} />
              {isBlocked(msg) ? <span class="small danger-text">{tr('CL-COOP-BAD-TEXT')}</span> : <span class="small muted">{msg.length} / 120</span>}
            </label>
          ) : null}
          {c.joinMode === 'request' && !c.myRole ? <span class="small muted">{tr('CL-COOP-REQ-NOTE')}</span> : null}
        </div>
      )}
    </Sheet>
  );
}

// ---------- §4 SCR-COOP-CREATE ----------
export function CoopCreateScreen(p: ScreenProps) {
  const app = useApp();
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [mode, setMode] = useState<CoopCard['joinMode']>('open');
  const [minLevel, setMinLevel] = useState(1);
  const [taken, setTaken] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const idem = useIdem(app);
  const cost = coopPrices().create;
  const nuts = wallet.value?.nuts ?? 0;
  const problem = nameProblem(name);
  const bad = isBlocked(name);
  // Уникальность — по мере ввода через 500 мс (ux/07 §4): поиск по названию, совпадение без регистра.
  useEffect(() => {
    setTaken(null);
    if (problem || bad) return;
    const n = name.trim().toLowerCase();
    const t = setTimeout(() => {
      app.call('listCoops', { query: { q: name.trim(), limit: 20 } })
        .then((r) => setTaken(r.items.some((c) => c.name.toLowerCase() === n)))
        .catch(() => setTaken(null));
    }, 500);
    return () => clearTimeout(t);
  }, [name]);
  const dirty = !!(name || desc);
  useEffect(() => {
    app.nav.intercept(p.entryKey, () => {
      if (!dirty) return false;
      app.nav.openSheet('MOD-CONFIRM', { title: tr('CL-COOP-LEAVE-FORM'), risky: tr('CL-COOP-LEAVE-FORM-GO'), safe: tr('CL-COOP-LEAVE-FORM-STAY'), onRisky: () => app.nav.pop() });
      return true;
    });
    return () => app.nav.intercept(p.entryKey, null);
  }, [dirty]);
  const valid = !problem && !bad && taken !== true && !isBlocked(desc);
  const short = cost > nuts ? cost - nuts : 0;
  const create = async () => {
    setBusy(true);
    try {
      const c = await app.call('createCoop', { body: { name: name.trim(), joinMode: mode, minLevel, ...(desc.trim() ? { description: desc.trim() } : {}) }, idempotencyKey: idem.key() });
      idem.next();
      setMyCoop(c);
      void app.call('getWallet').then(setWallet).catch(() => {});
      app.haptic('success');
      app.nav.intercept(p.entryKey, null);
      app.nav.selectTab('coop');
      showToast(tr('CL-COOP-CREATED'), 'success');
    } catch (e) {
      const code = errCode(e);
      if (code === 'NAME_TAKEN') setTaken(true);
      else app.handleError(e);
    } finally { setBusy(false); }
  };
  useActionBar({
    back: true,
    main: short
      ? { text: tr('CL-COOP-NEED-NUTS', { n: num(short) }), disabled: true, testId: 'create-go', onClick: () => {} }
      : { text: cost ? tr('CL-COOP-CREATE-FOR', { cost: amount('nuts', cost) }) : tr('COOP-BTN-CREATE'), testId: 'create-go', disabled: !valid || busy || !online.value, onClick: create },
  });
  const nameErr = name && problem ? tr(`CL-COOP-NAME-${problem.toUpperCase()}`) : bad ? tr('CL-COOP-NAME-BAD') : taken ? tr('CL-COOP-NAME-TAKEN') : null;
  return (
    <>
      <ScreenTitle>{tr('COOP-BTN-CREATE')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-COOP-CREATE">
        <div class="page col">
          <label class="col" style={{ gap: 4 }}>
            <b>{tr('COOP-CREATE-NAME')}</b>
            <input class="input" data-testid="create-name" maxLength={20} value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
            <span class={`small ${nameErr ? 'danger-text' : 'muted'}`} data-testid="create-name-note">{nameErr ?? (taken === false ? tr('CL-COOP-NAME-FREE') : tr('COOP-CREATE-HINT'))}</span>
          </label>
          <label class="col" style={{ gap: 4 }}>
            <b>{tr('CL-COOP-DESC')}</b>
            <textarea class="input" rows={2} maxLength={120} value={desc} onInput={(e) => setDesc((e.target as HTMLTextAreaElement).value)} />
            <span class={`small ${isBlocked(desc) ? 'danger-text' : 'muted'}`}>{isBlocked(desc) ? tr('CL-COOP-BAD-TEXT') : `${desc.length} / 120`}</span>
          </label>
          <div class="col" style={{ gap: 4 }}>
            <b>{tr('CL-COOP-JOIN-MODE')}</b>
            <div class="chips">{(['open', 'request', 'closed'] as const).map((m) => <button key={m} class={`chip ${mode === m ? 'on' : ''}`} aria-pressed={mode === m} data-testid={`create-mode-${m}`} onClick={() => setMode(m)}>{tr(JOIN_KEY[m])}</button>)}</div>
          </div>
          <label class="col" style={{ gap: 4 }}>
            <b>{tr('CL-COOP-MIN-LVL', { n: minLevel })}</b>
            <input type="range" min={1} max={30} value={minLevel} onInput={(e) => setMinLevel(Number((e.target as HTMLInputElement).value))} />
          </label>
          <span class="small muted">{tr('CL-COOP-EMBLEM-SOON')}</span>
        </div>
      </div>
    </>
  );
}

// ---------- §5 SCR-COOP-HOME ----------
function CoopHomeScreen(_p: ScreenProps) {
  const app = useApp();
  const now = useNow(30_000);
  const [err, setErr] = useState(false);
  const refresh = () => { setErr(false); reloadMine(app).catch(() => setErr(true)); };
  useEffect(refresh, []);
  const c = myCoop.value;
  const role = c?.myRole ?? coopBrief.value?.role ?? 'member';
  const lead = isLeader(role);
  const board = useLoad(() => app.call('getCoopBoard', { params: { coopId: coopBrief.value!.coopId } }));
  const shop = useLoad(() => app.call('getCoopShop', { params: { coopId: coopBrief.value!.coopId } }));
  const chat = useLoad(() => app.call('getChat', { params: { channel: `coop-${coopBrief.value!.coopId}` }, query: { limit: 2 } }));
  useActionBar({});
  const share = () => {
    const link = `${GAME_LINK}?startapp=coop_${c?.coopId ?? coopBrief.value?.coopId}`;
    app.wa?.openTelegramLink?.(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(tr('CL-COOP-SHARE-TEXT', { name: c?.name ?? '' }))}`);
  };
  const pct = c && c.buildPtsToNext ? Math.min(100, (c.buildPts / (c.buildPts + c.buildPtsToNext)) * 100) : 100;
  return (
    <>
      <ScreenTitle right={lead ? <button class="chip" data-testid="coop-settings" onClick={() => app.nav.push('SCR-COOP-SETTINGS')}>{tr('CL-COOP-SETTINGS')}</button> : null}>
        {c?.name ?? coopBrief.value?.name}
      </ScreenTitle>
      <div class="content" data-testid="SCR-COOP-HOME">
        <div class="page col coop-page">
          {err ? <ErrorBanner what={tr('CL-COOP-WHAT')} onRetry={refresh} /> : null}
          {!c ? <Skeleton h={90} n={3} /> : (
            <>
              <div class="coop-plate row" style={{ gap: 12 }} data-testid="coop-head">
                <Emblem size={64} />
                <div class="col" style={{ flex: 1, gap: 4 }}>
                  <span>{tr('CL-COOP-LVL', { n: c.level })} · {tr('COOP-MEMBERS', { n: c.membersCount, max: c.capacity })} · {tr(ROLE_KEY[role])}</span>
                  <div class="progress"><i style={{ width: `${pct}%` }} /></div>
                  <span class="small muted">{c.buildPtsToNext === null ? tr('CL-COOP-MAX-LVL') : tr('COOP-BUILD-LEVEL', { lvl: c.level + 1, cur: num(c.buildPts), need: num(c.buildPts + c.buildPtsToNext) })}</span>
                </div>
              </div>
              <DisbandBanner c={c} now={now} />
              {c.status === 'frozen' ? <div class="banner">{tr('CL-COOP-FROZEN')}</div> : null}
              {c.activity === 'dormant' ? <div class="banner warn">{tr('CL-COOP-DORMANT-BANNER', { n: c.active7d })}</div> : null}
              {c.npcChairman ? <div class="banner">{tr('CL-COOP-NPC-NOTE')}</div> : null}
              {lead && c.requests?.length ? <button class="list-row accent-row" data-testid="coop-requests" onClick={() => app.nav.push('SCR-COOP-REQUESTS')}><span style={{ flex: 1 }}>{tr('CL-COOP-NEW-REQUESTS', { n: c.requests.length })}</span>›</button> : null}
              {c.tgGroupUrl
                ? <button class="list-row" data-testid="coop-tg-open" onClick={() => app.wa?.openTelegramLink?.(c.tgGroupUrl!)}><span style={{ flex: 1 }}>{tr('COOP-TG-LINK', { coop: c.name })}</span>{lead ? <span class="chip" onClick={(e) => { e.stopPropagation(); app.nav.openSheet('MOD-TG-GROUP'); }}>{tr('CL-COOP-SETTINGS')}</span> : '›'}</button>
                : lead ? <button class="list-row" data-testid="coop-tg-link" onClick={() => app.nav.openSheet('MOD-TG-GROUP')}><span style={{ flex: 1 }}>{tr('CL-TG-LINK-ROW')}</span>›</button> : null}
              <BoardBlock board={board.data} loading={board.loading} error={!!board.error} retry={board.reload} />
              <ChoresBlock coopId={c.coopId} />
              <button class="list-row" data-testid="coop-build" onClick={() => app.nav.push('SCR-COOP-BUILD')}>
                <span class="col" style={{ flex: 1, gap: 2 }}><b>{tr('COOP-BUILD-TITLE')}</b><span class="small muted">{tr('COOP-BUILD-PLAN', { n: c.plansAvailable })}</span></span>
                <span class="btn small accent" style={{ width: 'auto' }}>{tr('COOP-BUILD-DONATE')}</span>
              </button>
              <button class="list-row" data-testid="coop-storage" onClick={() => app.nav.push('SCR-COOP-STORAGE')}>
                <span class="col" style={{ flex: 1, gap: 2 }}><b>{tr('COOP-STORE')}</b><span class="small muted">{tr('CL-COOP-STORE-PTS', { n: num(c.buildPts) })} · {tr('CL-COOP-SUMMONS', { n: c.summons })}</span></span>›
              </button>
              <button class="list-row" data-testid="coop-shop" onClick={() => app.nav.openSheet('MOD-COOP-SHOP')}>
                <span class="col" style={{ flex: 1, gap: 2 }}><b>{tr('COOP-LAVKA')}</b><span class="small muted">{shop.data ? tr('CL-COOP-MARKS', { n: shop.data.marks, cap: shop.data.marksCap }) : '…'}</span></span>›
              </button>
              <button class="list-row" data-testid="coop-members" onClick={() => app.nav.push('SCR-COOP-MEMBERS')}>
                <span class="col" style={{ flex: 1, gap: 2 }}><b>{tr('CL-COOP-MEMBERS-TITLE')}</b><span class="small muted ellipsis">{c.members.slice(0, 5).map((m) => m.displayName).join(', ')}</span></span>
                <span class="small">{tr('CL-COOP-ALL', { n: c.members.length })}</span>
              </button>
              <button class="list-row" data-testid="coop-chat" onClick={() => app.nav.push('SCR-COOP-CHAT')}>
                <span class="col" style={{ flex: 1, gap: 2, minWidth: 0 }}>
                  <b>{tr('COOP-CHAT-TITLE')}</b>
                  {chat.data?.items.filter((m) => !m.hidden).slice(-2).map((m) => <span key={m.id} class="small muted ellipsis">{m.author.displayName}: {m.body}</span>)}
                  {chat.data && !chat.data.items.length ? <span class="small muted">{tr('COOP-CHAT-EMPTY')}</span> : null}
                </span>›
              </button>
              <button class="list-row" data-testid="coop-invite" onClick={share}><span style={{ flex: 1 }}>{tr('CL-COOP-INVITE')}</span>›</button>
              <button class="link-btn small muted" data-testid="coop-leave" onClick={() => app.nav.openSheet('MOD-COOP-LEAVE')}>{tr('CL-COOP-LEAVE')}</button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/** Плашка назначенного роспуска (ux/07 §5.2, ВР-75). */
function DisbandBanner(p: { c: Coop; now: number }) {
  const app = useApp();
  if (p.c.status !== 'disbanding' || !p.c.disbandAt) return null;
  const left = Date.parse(p.c.disbandAt) - p.now;
  const v = p.c.disbandVote;
  return (
    <button class="banner danger" data-testid="coop-disband-banner" onClick={() => (v ? app.nav.openSheet('MOD-COOP-DISBAND-VOTE') : undefined)}>
      <span class="col" style={{ flex: 1, gap: 2, textAlign: 'left' }}>
        <b>{tr('CL-COOP-DISBAND-PENDING', { time: duration(Math.max(0, left)) })}</b>
        <span class="small">{v ? tr('CL-COOP-VOTE-TALLY', { k: v.against, need: v.needed }) : tr('CL-COOP-VOTE-NO-DEPUTIES')}</span>
      </span>
      {v && p.c.myRole === 'deputy' && !v.myVote ? <span class="btn small" style={{ width: 'auto' }}>{tr('CL-COOP-VOTE-AGAINST')}</span> : null}
      {v && p.c.myRole === 'chairman' ? <span class="btn small" style={{ width: 'auto' }}>{tr('CL-COOP-VOTE-CANCEL')}</span> : null}
    </button>
  );
}

/**
 * Доска объявлений: общие задания дня (ux/07 §5.1, E07-T16). Строк столько, сколько прислал сервер.
 * «Идти» — туда, где делается шаг (ГД 09 §4.2): Q-C-01…05 — бои (карта), 06–07 — стройка, 08 — выезды, 09 — сумка.
 */
export function boardTarget(defId: string): 'map' | 'quests' | 'SCR-COOP-BUILD' | 'SCR-TRIPS' | 'SCR-INVENTORY' | 'SCR-COOP-MEMBERS' {
  const n = Number(defId.slice(4));
  if (n === 6 || n === 7) return 'SCR-COOP-BUILD';
  if (n === 8) return 'SCR-TRIPS';
  if (n === 9) return 'SCR-INVENTORY';
  if (n === 10) return 'quests'; // «все 5» ежедневных
  if (n === 12) return 'SCR-COOP-MEMBERS'; // в гости к соседям по кооперативу
  return 'map';
}

/** Название задания Доски; новое задание сервера без строки — общее «Задание кооператива», не сырой ключ (Д-5). */
export function boardTaskName(defId: string): string {
  const k = `CL-COOP-TASK-${defId}`;
  return hasKey(k) ? tr(k) : tr('CL-COOP-TASK-UNKNOWN');
}
function BoardBlock(p: { board: Board | null; loading: boolean; error: boolean; retry: () => void }) {
  const app = useApp();
  const now = useNow(60_000);
  const go = (defId: string) => {
    const t = boardTarget(defId);
    if (t === 'map' || t === 'quests') app.nav.selectTab(t); else app.nav.push(t);
  };
  return (
    <div class="coop-plate col" data-testid="coop-board">
      <div class="row"><b style={{ flex: 1 }}>{tr('CL-COOP-BOARD')}</b>{p.board ? <span class="small muted">{tr('CL-COOP-BOARD-RESET', { time: duration(Math.max(0, Date.parse(p.board.resetsAt) - now)) })}</span> : null}</div>
      {p.error && !p.board ? <ErrorBanner what={tr('CL-COOP-BOARD')} onRetry={p.retry} /> : null}
      {!p.board && p.loading ? <Skeleton h={44} n={3} /> : null}
      {p.board?.tasks.map((t) => {
        const done = t.status === 'completed';
        return (
          <div key={t.taskId} class="board-row" data-testid={`board-${t.taskId}`}>
            <span class="col" style={{ flex: 1, gap: 2, minWidth: 0 }}>
              <span>{boardTaskName(t.defId)}</span>
              <span class="small muted">
                {done ? tr('CL-COOP-BOARD-DONE') : tr('CL-COOP-PROGRESS', { progress: num(t.progress), target: num(t.target) })}
                {t.mySteps ? ` · ${tr('CL-COOP-MY-STEPS', { n: t.mySteps })}` : ''}
                {' · '}{Object.entries(t.rewards.member.currencies ?? {}).map(([k, v]) => amount(k, v)).join(', ')}
              </span>
              {!done ? <div class="progress thin"><i style={{ width: `${Math.min(100, (t.progress / t.target) * 100)}%` }} /></div> : null}
            </span>
            {done ? <I n="check" size={22} /> : t.myEligible ? <button class="btn small outline" style={{ width: 'auto' }} data-testid={`board-go-${t.taskId}`} onClick={() => go(t.defId)}>{tr('CL-COOP-GO')}</button> : <span class="small muted">{tr('CL-COOP-TENURE')}</span>}
          </div>
        );
      })}
    </div>
  );
}

/** Наряды дня (ГД 07 §6.3): 5 энергии, мгновенно; остаток на сегодня — из ответа сервера. */
function ChoresBlock(p: { coopId: string }) {
  const app = useApp();
  const [left, setLeft] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const idem = useIdem(app);
  const energy = wallet.value?.energy.value ?? 0;
  const run = async (choreId: string) => {
    setBusy(choreId);
    try {
      const r = await app.call('doChore', { params: { coopId: p.coopId }, body: { choreId }, idempotencyKey: idem.key() });
      idem.next();
      setWallet(r.wallet);
      setLeft(r.choresLeftToday);
      app.haptic('success');
      showToast(tr('CL-COOP-CHORE-DONE', { what: Object.entries(r.rewards.currencies ?? {}).map(([k, v]) => amount(k, v)).join(', '), pts: num(r.buildPtsAdded) }), 'success');
    } catch (e) {
      const code = errCode(e);
      if (code === 'DAILY_LIMIT') setLeft(0);
      else if (code === 'NOT_ENOUGH_ENERGY') app.nav.openSheet('MOD-ENERGY', { need: CHORE_ENERGY });
      else if (code === 'TENURE_TOO_SHORT') showToast(tr('CL-COOP-CHORE-TENURE'));
      else app.handleError(e);
    } finally { setBusy(null); }
  };
  return (
    <div class="coop-plate col" data-testid="coop-chores">
      <div class="row"><b style={{ flex: 1 }}>{tr('COOP-ORDERS')}</b>{left !== null ? <span class="small muted">{tr('CL-COOP-CHORES-LEFT', { n: left })}</span> : null}</div>
      <div class="row chores">
        {CHORES.map((id) => (
          <button key={id} class="btn small outline chore" data-testid={`chore-${id}`} disabled={!online.value || left === 0 || !!busy || energy < CHORE_ENERGY} onClick={() => run(id)}>
            <span>{tr(CHORE_KEY(id))}</span><span class="small">{CHORE_ENERGY} <I n="energy" size={14} /></span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- §7 SCR-COOP-MEMBERS и MOD-MEMBER ----------
export function CoopMembersScreen(_p: ScreenProps) {
  const app = useApp();
  const c = myCoop.value;
  const now = useNow(60_000);
  const [q, setQ] = useState('');
  useEffect(() => { void reloadMine(app).catch(() => {}); }, []);
  useActionBar({ back: true });
  const list = (c?.members ?? []).filter((m) => !q || m.displayName.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role] || (b.weekPts ?? 0) - (a.weekPts ?? 0) || b.level - a.level);
  const deputies = c?.members.filter((m) => m.role === 'deputy').length ?? 0;
  return (
    <>
      <ScreenTitle>{tr('CL-COOP-MEMBERS-TITLE')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-COOP-MEMBERS">
        <div class="page col">
          {!c ? <Skeleton h={56} n={5} /> : (
            <>
              <span class="small muted">{tr('CL-COOP-MEMBERS-SUM', { n: c.membersCount, max: c.capacity, d: deputies, dmax: deputyLimit(c.level) })}</span>
              {c.members.length >= 20 ? <input class="input" type="search" placeholder={tr('CL-COOP-MEMBER-SEARCH')} value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} /> : null}
              <div class="list col">
                {list.map((m) => {
                  const fresh = now - Date.parse(m.joinedAt) < 86_400_000;
                  return (
                    <button key={m.publicId} class="list-row" data-testid={`member-${m.publicId}`} onClick={() => app.nav.openSheet('MOD-MEMBER', { publicId: m.publicId })}>
                      <span class="col" style={{ flex: 1, gap: 2 }}>
                        <span>{m.displayName}{fresh ? <span class="badge soft"> {tr('CL-COOP-NEWBIE')}</span> : null}</span>
                        <span class="small muted">{tr(ROLE_KEY[m.role])} · {tr('CL-COOP-LVL', { n: m.level })}{m.weekPts !== undefined ? ` · ${tr('CL-COOP-WEEK-PTS', { n: num(m.weekPts) })}` : ''}{m.lastSeenAt !== undefined ? ` · ${seenText(m.lastSeenAt, now)}` : ''}{m.kickEffectiveAt ? ` · ${tr('CL-COOP-KICK-PENDING', { time: duration(Math.max(0, Date.parse(m.kickEffectiveAt) - now)) })}` : ''}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/** «онлайн» — заходил за последние 5 минут; иначе «был N назад» (ux/07 §7). */
export function seenText(lastSeenAt: string | null, now: number): string {
  if (!lastSeenAt) return tr('CL-COOP-SEEN-LONG');
  const ago = now - Date.parse(lastSeenAt);
  if (ago < 5 * 60_000) return tr('CL-COOP-ONLINE');
  if (ago < 3_600_000) return tr('CL-COOP-SEEN-MIN', { n: Math.floor(ago / 60_000) });
  if (ago < 86_400_000) return tr('CL-COOP-SEEN-H', { n: Math.floor(ago / 3_600_000) });
  return tr('CL-COOP-SEEN-D', { n: Math.floor(ago / 86_400_000) });
}

/** Заместителей не больше 2 + ⌊L/10⌋, максимум 6 (ux/07 §0). */
export function deputyLimit(level: number): number { return Math.min(6, 2 + Math.floor(level / 10)); }

export function MemberSheet({ entry }: SheetProps) {
  const app = useApp();
  const c = myCoop.value;
  const pid = String(entry.params['publicId']);
  const m = c?.members.find((x) => x.publicId === pid);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const me = player.value?.publicId;
  const role = c?.myRole;
  const frozen = c?.status === 'frozen';
  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try {
      await fn();
      await reloadMine(app);
      if (ok) showToast(ok, 'success');
      app.nav.popSheet();
    } catch (e) {
      const code = errCode(e);
      if (code === 'ELECTION_ACTIVE') setNote(tr('CL-COOP-ELECTION-LOCK'));
      else if (code === 'COOP_FROZEN') setNote(tr('CL-COOP-FROZEN-LOCK'));
      else if (code === 'DISBAND_SCHEDULED') setNote(tr('CL-COOP-DEPUTY-LOCK'));
      else if (code === 'DAILY_LIMIT') setNote(tr('CL-COOP-KICK-LIMIT'));
      else app.handleError(e);
      void reloadMine(app).catch(() => {});
    } finally { setBusy(false); }
  };
  if (!c || !m) return <Sheet entry={entry} title="…"><Skeleton h={44} n={2} /></Sheet>;
  const coopId = c.coopId;
  const self = m.publicId === me;
  const deputies = c.members.filter((x) => x.role === 'deputy').length;
  const tenure3 = Date.now() - Date.parse(m.joinedAt) >= 3 * 86_400_000;
  const off = busy || !online.value || frozen;
  const setRole = (r: CoopRole) => act(() => app.call('setCoopRole', { params: { coopId, publicId: pid }, body: { role: r } }));
  const actions: { text: string; onClick: () => unknown; disabled?: boolean; tone?: 'outline' | 'danger'; testId?: string }[] = [
    { text: tr('UI-NAV-PROFILE'), tone: 'outline', testId: 'member-profile', onClick: () => { app.nav.closeSheets(); app.nav.push('SCR-GARAGE-VISIT', { publicId: pid }); } },
  ];
  if (!self && role === 'chairman') {
    if (m.role === 'member' && deputies < deputyLimit(c.level)) actions.push({ text: tr('COOP-PROMOTE'), testId: 'member-promote', disabled: off, onClick: () => setRole('deputy') });
    if (m.role === 'deputy') actions.push({ text: tr('COOP-DEMOTE'), testId: 'member-demote', disabled: off, onClick: () => setRole('member') });
    if (tenure3) actions.push({ text: tr('COOP-TRANSFER'), testId: 'member-transfer', disabled: off, onClick: () => app.nav.openSheet('MOD-CONFIRM', {
      title: tr('COOP-TRANSFER-CONFIRM', { player: m.displayName }), text: tr('CL-COOP-TRANSFER-NOTE'), risky: tr('COOP-TRANSFER'), armMs: 1500,
      onRisky: () => { app.haptic('heavy'); void act(() => app.call('setCoopRole', { params: { coopId, publicId: pid }, body: { role: 'chairman' } })); },
    }) });
  }
  const canKick = !self && (role === 'chairman' || (role === 'deputy' && m.role === 'member'));
  if (canKick && !m.kickEffectiveAt) actions.push({ text: tr('COOP-KICK'), tone: 'danger', testId: 'member-kick', disabled: off, onClick: () => app.nav.openSheet('MOD-CONFIRM', {
    title: tr('CL-COOP-KICK-CONFIRM', { name: m.displayName }), risky: tr('COOP-KICK'),
    onRisky: () => { void act(() => app.call('kickMember', { params: { coopId, publicId: pid }, body: {} })); },
  }) });
  if (canKick && m.kickEffectiveAt) actions.push({ text: tr('CL-COOP-KICK-CANCEL'), tone: 'outline', testId: 'member-unkick', disabled: off, onClick: () => act(() => app.call('cancelKick', { params: { coopId, publicId: pid } })) });
  return (
    <Sheet entry={entry} title={m.displayName} actions={actions} testId="MOD-MEMBER">
      <div class="col">
        <span class="muted">{tr(ROLE_KEY[m.role])} · {tr('CL-COOP-LVL', { n: m.level })}</span>
        {frozen ? <span class="small muted">{tr('CL-COOP-FROZEN-LOCK')}</span> : null}
        {note ? <span class="small danger-text" data-testid="member-note">{note}</span> : null}
      </div>
    </Sheet>
  );
}

// ---------- §8 SCR-COOP-REQUESTS ----------
export function CoopRequestsScreen(_p: ScreenProps) {
  const app = useApp();
  const c = myCoop.value;
  const [busy, setBusy] = useState(false);
  const reqs = c?.requests ?? [];
  const free = c ? c.capacity - c.membersCount : 0;
  const decide = async (reqId: string, accept: boolean) => {
    if (!c) return;
    setBusy(true);
    try {
      await app.call('decideRequest', { params: { coopId: c.coopId, reqId }, body: { accept } });
      app.haptic(accept ? 'success' : 'light');
      if (!accept) showToast(tr('CL-COOP-DECLINED'));
    } catch (e) {
      const code = errCode(e);
      if (code === 'NOT_FOUND') showToast(tr('CL-COOP-REQ-GONE'));
      else if (code === 'COOP_FULL') showToast(tr('CL-COOP-FULL-NOW'));
      else app.handleError(e);
    } finally {
      await reloadMine(app).catch(() => {});
      setBusy(false);
    }
  };
  const acceptAll = async () => { for (const r of reqs) await decide(r.reqId, true); };
  useActionBar({
    back: true,
    ...(reqs.length > 1 ? { main: free >= reqs.length
      ? { text: tr('CL-COOP-ACCEPT-ALL', { n: reqs.length }), testId: 'req-accept-all', disabled: busy || !online.value, onClick: acceptAll }
      : { text: tr('CL-COOP-SLOTS-REQS', { free, n: reqs.length }), disabled: true, onClick: () => {} } } : {}),
  });
  return (
    <>
      <ScreenTitle>{tr('CL-COOP-REQUESTS')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-COOP-REQUESTS">
        <div class="page col">
          {!reqs.length ? <div class="empty" data-testid="req-empty">{tr('CL-COOP-NO-REQUESTS')}</div> : reqs.map((r) => (
            <div key={r.reqId} class="card col" data-testid={`req-${r.reqId}`}>
              <div class="row"><b style={{ flex: 1 }}>{r.displayName}</b><span class="small muted">{tr('CL-COOP-LVL', { n: r.level })}</span></div>
              {r.message ? <span class="small">«{r.message}»</span> : null}
              <span class="small muted">{tr('CL-COOP-REQ-LEFT', { time: duration(Math.max(0, Date.parse(r.createdAt) + 48 * 3_600_000 - Date.now())) })}</span>
              <div class="row" style={{ gap: 8 }}>
                <button class="btn small outline" data-testid={`req-decline-${r.reqId}`} disabled={busy || !online.value} onClick={() => decide(r.reqId, false)}>{tr('CL-COOP-DECLINE')}</button>
                <button class="btn small accent" data-testid={`req-accept-${r.reqId}`} disabled={busy || !online.value || free <= 0} onClick={() => decide(r.reqId, true)}>{tr('CL-COOP-ACCEPT')}</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ---------- §13 SCR-COOP-SETTINGS ----------
/** Цены кооператива из bootstrap (Р-87): создание и переименование — гайки, эмблема — талоны. */
export function coopPrices(): { create: number; rename: number; emblem: number } {
  const v = (clientConfig.value['coop'] ?? {}) as { createCostNuts?: number; renameCostNuts?: number; emblemCostTalons?: number };
  return { create: Number(v.createCostNuts ?? 0), rename: Number(v.renameCostNuts ?? 0), emblem: Number(v.emblemCostTalons ?? 0) };
}

/** Изменённые поля формы настроек — только они уходят в PATCH. */
export function settingsDiff(c: Pick<Coop, 'name' | 'joinMode' | 'minLevel' | 'description'> & { pinnedMessage?: string | null }, f: { name: string; joinMode: Coop['joinMode']; minLevel: number; description: string; pinnedMessage: string }) {
  const d: { name?: string; joinMode?: Coop['joinMode']; minLevel?: number; description?: string | null; pinnedMessage?: string | null } = {};
  if (f.name.trim() !== c.name) d.name = f.name.trim();
  if (f.joinMode !== c.joinMode) d.joinMode = f.joinMode;
  if (f.minLevel !== c.minLevel) d.minLevel = f.minLevel;
  if (f.description.trim() !== (c.description ?? '')) d.description = f.description.trim() || null;
  if (f.pinnedMessage.trim() !== (c.pinnedMessage ?? '')) d.pinnedMessage = f.pinnedMessage.trim() || null;
  return d;
}

/** Председатель — всё; заместитель — приём, мин. уровень, описание, закреплённое (ux/07 §13; Р-87). */
export function CoopSettingsScreen(p: ScreenProps) {
  const app = useApp();
  const c = myCoop.value;
  const now = useNow(60_000);
  const fromCoop = (x: Coop | null) => ({ name: x?.name ?? '', joinMode: x?.joinMode ?? 'open', minLevel: x?.minLevel ?? 1, description: x?.description ?? '', pinnedMessage: x?.pinnedMessage ?? '' });
  const [f, setF] = useState(() => fromCoop(c));
  const touched = useRef(false);
  // Кооператив догрузился после открытия экрана — заполнить форму, пока игрок ничего не трогал.
  useEffect(() => { if (!touched.current && c) setF(fromCoop(c)); }, [c?.coopId, c?.name, c?.pinnedMessage]);
  useEffect(() => { void reloadMine(app).catch(() => {}); }, []);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const idem = useIdem(app);
  const chair = c?.myRole === 'chairman';
  const diff = c ? settingsDiff(c, f) : {};
  const dirty = Object.keys(diff).length > 0;
  const renameLock = c?.nextRenameAt && Date.parse(c.nextRenameAt) > now ? Date.parse(c.nextRenameAt) : 0;
  const price = coopPrices();
  const nameErr = diff.name !== undefined ? (nameProblem(f.name) ? tr(`CL-COOP-NAME-${nameProblem(f.name)!.toUpperCase()}`) : isBlocked(f.name) ? tr('CL-COOP-NAME-BAD') : null) : null;
  const textBad = isBlocked(f.description) || isBlocked(f.pinnedMessage);
  useEffect(() => {
    app.nav.intercept(p.entryKey, () => {
      if (!dirty) return false;
      app.nav.openSheet('MOD-CONFIRM', { title: tr('CL-COOP-LEAVE-FORM'), risky: tr('CL-COOP-LEAVE-FORM-GO'), safe: tr('CL-COOP-LEAVE-FORM-STAY'), onRisky: () => { app.nav.intercept(p.entryKey, null); app.nav.pop(); } });
      return true;
    });
    return () => app.nav.intercept(p.entryKey, null);
  }, [dirty]);
  const save = async () => {
    if (!c) return;
    setBusy(true);
    setNote(null);
    try {
      const r = await app.call('updateCoop', { params: { coopId: c.coopId }, body: diff, idempotencyKey: idem.key() });
      idem.next();
      setMyCoop(r);
      if (diff.name) void app.call('getWallet').then(setWallet).catch(() => {});
      app.haptic('success');
      showToast(tr('CL-COOP-SAVED'), 'success');
      setF({ name: r.name, joinMode: r.joinMode, minLevel: r.minLevel, description: r.description ?? '', pinnedMessage: r.pinnedMessage ?? '' });
    } catch (e) {
      const code = errCode(e);
      const det = (e as ApiCallError).details ?? {};
      if (code === 'NAME_TAKEN') setNote(tr('CL-COOP-NAME-TAKEN'));
      else if (code === 'NAME_INVALID') setNote(tr('CL-COOP-NAME-BAD'));
      else if (code === 'COOLDOWN') setNote(tr('CL-COOP-RENAME-NEXT', { date: dateMsk(Date.parse(String(det['availableAt'] ?? ''))) }));
      else if (code === 'COOP_FROZEN') setNote(tr('CL-COOP-FROZEN-LOCK'));
      else if (code === 'FORBIDDEN_ROLE') setNote(tr('COOP-ROLE-DENIED'));
      else app.handleError(e);
    } finally { setBusy(false); }
  };
  const confirmSave = () => (diff.name && price.rename
    ? app.nav.openSheet('MOD-CONFIRM', { title: tr('CL-COOP-RENAME-Q', { name: f.name.trim(), cost: amount('nuts', price.rename) }), risky: tr('CL-COOP-SAVE'), onRisky: save })
    : save());
  useActionBar({ back: true, main: { text: tr('CL-COOP-SAVE'), testId: 'settings-save', disabled: !dirty || busy || !online.value || !!nameErr || textBad || c?.status === 'frozen', onClick: confirmSave } });
  if (!c) return <div class="content screen-bg"><Skeleton h={44} n={4} /></div>;
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => { touched.current = true; setF({ ...f, [k]: v }); setNote(null); };
  return (
    <>
      <ScreenTitle>{tr('CL-COOP-SETTINGS')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-COOP-SETTINGS">
        <div class="page col">
          {c.status === 'frozen' ? <div class="banner warn">{tr('CL-COOP-FROZEN-LOCK')}</div> : null}
          {chair ? (
            <label class="col" style={{ gap: 4 }}>
              <b>{tr('COOP-CREATE-NAME')}</b>
              <input class="input" data-testid="settings-name" maxLength={20} disabled={!!renameLock} value={f.name} onInput={(e) => set('name', (e.target as HTMLInputElement).value)} />
              <span class={`small ${nameErr ? 'danger-text' : 'muted'}`}>{nameErr ?? (renameLock ? tr('CL-COOP-RENAME-NEXT', { date: dateMsk(renameLock) }) : price.rename ? tr('CL-COOP-RENAME-COST', { cost: amount('nuts', price.rename) }) : tr('COOP-CREATE-HINT'))}</span>
            </label>
          ) : <div class="card col"><span class="small muted">{tr('COOP-CREATE-NAME')}</span><b>{c.name}</b></div>}
          <div class="col" style={{ gap: 4 }}>
            <b>{tr('CL-COOP-JOIN-MODE')}</b>
            <div class="chips">{(['open', 'request', 'closed'] as const).map((m) => <button key={m} class={`chip ${f.joinMode === m ? 'on' : ''}`} aria-pressed={f.joinMode === m} data-testid={`settings-mode-${m}`} onClick={() => set('joinMode', m)}>{tr(JOIN_KEY[m])}</button>)}</div>
          </div>
          <label class="col" style={{ gap: 4 }}>
            <b>{tr('CL-COOP-MIN-LVL', { n: f.minLevel })}</b>
            <input type="range" min={1} max={30} value={f.minLevel} onInput={(e) => set('minLevel', Number((e.target as HTMLInputElement).value))} />
          </label>
          <label class="col" style={{ gap: 4 }}>
            <b>{tr('CL-COOP-DESC')}</b>
            <textarea class="input" rows={2} maxLength={120} value={f.description} onInput={(e) => set('description', (e.target as HTMLTextAreaElement).value)} />
            {isBlocked(f.description) ? <span class="small danger-text">{tr('CL-COOP-BAD-TEXT')}</span> : null}
          </label>
          <label class="col" style={{ gap: 4 }}>
            <b>{tr('CL-COOP-PINNED')}</b>
            <textarea class="input" data-testid="settings-pinned" rows={2} maxLength={300} value={f.pinnedMessage} onInput={(e) => set('pinnedMessage', (e.target as HTMLTextAreaElement).value)} />
            {isBlocked(f.pinnedMessage) ? <span class="small danger-text">{tr('CL-COOP-BAD-TEXT')}</span> : null}
          </label>
          {chair ? <span class="small muted">{tr('CL-COOP-EMBLEM-COST', { cost: amount('talons', price.emblem) })}</span> : null}
          {note ? <span class="small danger-text" data-testid="settings-note">{note}</span> : null}
          <button class="list-row" onClick={() => app.nav.openSheet('MOD-TG-GROUP')}><span style={{ flex: 1 }}>{c.tgGroupUrl ? tr('COOP-TG-LINK', { coop: c.name }) : tr('CL-TG-LINK-ROW')}</span>›</button>
          {chair ? <button class="list-row" data-testid="settings-transfer" onClick={() => app.nav.push('SCR-COOP-MEMBERS')}><span style={{ flex: 1 }}>{tr('COOP-TRANSFER')}</span>›</button> : null}
          {chair ? <button class="list-row danger-text" data-testid="settings-disband" onClick={() => app.nav.openSheet('MOD-COOP-LEAVE', { disband: true })}><span style={{ flex: 1 }}>{tr('CL-COOP-DISBAND')}</span>›</button> : null}
        </div>
      </div>
    </>
  );
}

/** Дата по Москве «14.12» для подписей. */
function dateMsk(ms: number): string {
  return Number.isFinite(ms) ? new Date(ms).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Moscow' }) : '—';
}

// ---------- §14 MOD-COOP-LEAVE ⛔ ----------
export function CoopLeaveSheet({ entry }: SheetProps) {
  const app = useApp();
  const c = myCoop.value;
  const now = useNow(60_000);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const idem = useIdem(app);
  const disband = !!entry.params['disband'];
  const move = entry.params['move'] as string | undefined;
  if (!c) return <Sheet entry={entry} title="…"><Skeleton h={44} /></Sheet>;
  const others = c.members.length > 1;
  const chair = c.myRole === 'chairman';
  const close = () => app.nav.popSheet();
  const leave = async () => {
    setBusy(true);
    try {
      const r = await app.call('leaveCoop', { params: { coopId: c.coopId }, body: {} });
      if (r.rejoinAt) setRejoinAt(Date.parse(r.rejoinAt));
      setMyCoop(null);
      app.haptic('warning');
      app.nav.closeSheets();
      app.nav.selectTab('coop');
    } catch (e) {
      if (errCode(e) === 'CHAIRMAN_MUST_TRANSFER') showToast(tr('CL-COOP-TRANSFER-FIRST'));
      else app.handleError(e);
    } finally { setBusy(false); }
  };
  const dissolve = async () => {
    setBusy(true);
    try {
      const r = await app.call('dissolveCoop', { params: { coopId: c.coopId }, body: { confirm: true }, idempotencyKey: idem.key() });
      idem.next();
      app.haptic('warning');
      if (r.status === 'disbanded') { setMyCoop(null); app.nav.closeSheets(); app.nav.selectTab('coop'); return; }
      await reloadMine(app);
      app.nav.closeSheets();
    } catch (e) {
      const code = errCode(e);
      if (code === 'COOLDOWN') showToast(tr('CL-COOP-DISBAND-COOLDOWN'));
      else if (code === 'ELECTION_ACTIVE') showToast(tr('CL-COOP-LEAVE-ELECTION'));
      else app.handleError(e);
    } finally { setBusy(false); }
  };
  let title = tr('CL-COOP-LEAVE-TITLE');
  let body: ComponentChildren;
  let actions: { text: string; onClick: () => unknown; disabled?: boolean; tone?: 'outline' | 'danger'; testId?: string }[];
  const off = busy || !online.value;
  if (disband && chair && assemblyWeek(now)) {
    body = <p>{tr('CL-COOP-DISBAND-ASSEMBLY')}</p>;
    actions = [{ text: tr('CL-COOP-OK'), onClick: close }];
  } else if (disband && chair) {
    const v = c.members.filter((m) => m.role === 'deputy').length;
    title = tr('CL-COOP-DISBAND');
    body = (
      <div class="col">
        <p style={{ margin: 0 }}>{others ? tr('CL-COOP-DISBAND-48') : tr('CL-COOP-DISBAND-NOW')}</p>
        {others && v ? <span class="small muted">{tr('CL-COOP-DISBAND-VOTE-NOTE', { need: Math.floor(v / 2) + 1, n: v })}</span> : null}
        <label class="col" style={{ gap: 4 }}><span class="small">{tr('CL-COOP-TYPE-NAME', { name: c.name })}</span>
          <input class="input" data-testid="disband-name" value={typed} onInput={(e) => setTyped((e.target as HTMLInputElement).value)} /></label>
      </div>
    );
    actions = [
      { text: others ? tr('CL-COOP-DISBAND-48-BTN') : tr('CL-COOP-DISBAND'), tone: 'danger', testId: 'disband-go', disabled: off || typed.trim() !== c.name, onClick: dissolve },
      { text: tr('CL-COOP-CANCEL'), tone: 'outline', onClick: close },
    ];
  } else if (chair && others) {
    body = <p>{tr('CL-COOP-TRANSFER-FIRST')}</p>;
    actions = [{ text: tr('CL-COOP-PICK-SUCCESSOR'), testId: 'leave-successor', onClick: () => { app.nav.closeSheets(); app.nav.push('SCR-COOP-MEMBERS'); } }, { text: tr('CL-COOP-CANCEL'), tone: 'outline', onClick: close }];
  } else if (chair) {
    // Председатель один — выход равен роспуску сразу (ux/07 §14).
    title = tr('CL-COOP-DISBAND');
    body = (
      <div class="col">
        <p style={{ margin: 0 }}>{tr('CL-COOP-DISBAND-NOW')}</p>
        <label class="col" style={{ gap: 4 }}><span class="small">{tr('CL-COOP-TYPE-NAME', { name: c.name })}</span>
          <input class="input" data-testid="disband-name" value={typed} onInput={(e) => setTyped((e.target as HTMLInputElement).value)} /></label>
      </div>
    );
    actions = [{ text: tr('CL-COOP-DISBAND'), tone: 'danger', testId: 'disband-go', disabled: off || typed.trim() !== c.name, onClick: dissolve }, { text: tr('CL-COOP-CANCEL'), tone: 'outline', onClick: close }];
  } else {
    const dormant = c.activity === 'dormant';
    body = <p>{move ? tr('CL-COOP-MOVE-TEXT', { old: c.name, name: move }) : dormant ? tr('CL-COOP-LEAVE-DORMANT') : tr('CL-COOP-LEAVE-TEXT', { name: c.name })}</p>;
    actions = [{ text: tr('CL-COOP-STAY'), testId: 'leave-stay', onClick: close }, { text: tr('COOP-BTN-LEAVE'), tone: 'danger', testId: 'leave-go', disabled: off, onClick: leave }];
  }
  return <Sheet entry={entry} title={title} actions={actions} testId="MOD-COOP-LEAVE">{body}</Sheet>;
}

// ---------- §14.1 MOD-COOP-DISBAND-VOTE ----------
export function DisbandVoteSheet({ entry }: SheetProps) {
  const app = useApp();
  const c = myCoop.value;
  const now = useNow(30_000);
  const [busy, setBusy] = useState(false);
  const [closed, setClosed] = useState<string | null>(null);
  const v = c?.disbandVote;
  if (!c || !v || !c.disbandAt) return <Sheet entry={entry} title={tr('CL-COOP-VOTE-TITLE')} actions={[{ text: tr('CL-COOP-OK'), onClick: () => app.nav.popSheet() }]}>{closed ?? tr('CL-COOP-VOTE-GONE')}</Sheet>;
  const vote = async (against: boolean) => {
    setBusy(true);
    try {
      const r = await app.call('voteDissolve', { params: { coopId: c.coopId }, body: { against } });
      app.haptic('success');
      showToast(tr('CL-COOP-VOTE-SAVED'), 'success');
      if (r.status === 'active') setClosed(tr('CL-COOP-VOTE-CANCELLED'));
      await reloadMine(app);
    } catch (e) {
      const code = errCode(e);
      if (code === 'NOT_SCHEDULED') { showToast(tr('CL-COOP-VOTE-GONE')); app.nav.popSheet(); }
      else if (code === 'CONFLICT') setClosed(tr('CL-COOP-VOTE-CLOSING'));
      else app.handleError(e);
      void reloadMine(app).catch(() => {});
    } finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try { await app.call('cancelDissolve', { params: { coopId: c.coopId }, body: {} }); setClosed(tr('CL-COOP-VOTE-CANCELLED-CHAIR')); await reloadMine(app); }
    catch (e) { app.handleError(e); } finally { setBusy(false); }
  };
  const off = busy || !online.value;
  const chair = c.myRole === 'chairman';
  const deputy = c.myRole === 'deputy';
  const until = new Date(Date.parse(v.endsAt)).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });
  const actions: { text: string; onClick: () => unknown; disabled?: boolean; tone?: 'outline' | 'danger'; testId?: string }[] = [];
  let text: string;
  if (closed) { text = closed; actions.push({ text: tr('CL-COOP-OK'), onClick: () => app.nav.popSheet() }); }
  else if (chair) { text = tr('CL-COOP-VOTE-CHAIR'); actions.push({ text: tr('CL-COOP-VOTE-CANCEL'), testId: 'vote-cancel', disabled: off, onClick: cancel }, { text: tr('UI-BTN-CLOSE'), tone: 'outline', onClick: () => app.nav.popSheet() }); }
  else if (deputy && v.myVote === 'against') { text = tr('CL-COOP-VOTE-MY-AGAINST', { until }); actions.push({ text: tr('CL-COOP-VOTE-CHANGE-NOT'), testId: 'vote-not-against', disabled: off, onClick: () => vote(false) }, { text: tr('UI-BTN-CLOSE'), tone: 'outline', onClick: () => app.nav.popSheet() }); }
  else if (deputy && v.myVote === 'not_against') { text = tr('CL-COOP-VOTE-MY-NOT', { until }); actions.push({ text: tr('CL-COOP-VOTE-AGAINST'), testId: 'vote-against', disabled: off, onClick: () => vote(true) }, { text: tr('UI-BTN-CLOSE'), tone: 'outline', onClick: () => app.nav.popSheet() }); }
  else if (deputy) { text = tr('CL-COOP-VOTE-RULE', { need: v.needed, n: v.deputies }); actions.push({ text: tr('CL-COOP-VOTE-AGAINST'), testId: 'vote-against', disabled: off, onClick: () => vote(true) }, { text: tr('CL-COOP-VOTE-NOT'), tone: 'outline', testId: 'vote-not-against', disabled: off, onClick: () => vote(false) }); }
  else { text = tr('CL-COOP-VOTE-MEMBER'); actions.push({ text: tr('UI-BTN-CLOSE'), tone: 'outline', onClick: () => app.nav.popSheet() }); }
  const chairName = c.members.find((m) => m.role === 'chairman')?.displayName ?? '';
  return (
    <Sheet entry={entry} title={tr('CL-COOP-VOTE-TITLE')} actions={actions} testId="MOD-COOP-DISBAND-VOTE">
      <div class="col">
        <span>{tr('CL-COOP-VOTE-BY', { name: chairName })} · {tr('CL-COOP-VOTE-ENDS', { time: duration(Math.max(0, Date.parse(c.disbandAt) - now)) })}</span>
        <div class="progress"><i style={{ width: `${Math.min(100, (v.against / v.needed) * 100)}%` }} /></div>
        <span class="small muted">{tr('CL-COOP-VOTE-TALLY', { k: v.against, need: v.needed })}</span>
        <p style={{ margin: 0 }}>{text}</p>
        {deputy && !closed ? <span class="small muted">{tr('CL-COOP-VOTE-LOSS')}</span> : null}
      </div>
    </Sheet>
  );
}

// ---------- §9 SCR-COOP-BUILD, MOD-COOP-DONATE ----------
export function CoopBuildScreen(_p: ScreenProps) {
  const app = useApp();
  const id = coopBrief.value?.coopId ?? '';
  const load = useLoad(() => app.call('getCoopBuildings', { params: { coopId: id } }));
  const c = myCoop.value;
  const lead = isLeader(c?.myRole);
  const [busy, setBusy] = useState(false);
  const idem = useIdem(app);
  useActionBar({ back: true, main: { text: tr('CL-COOP-DONATE'), testId: 'build-donate', disabled: !online.value, onClick: () => app.nav.openSheet('MOD-COOP-DONATE') } });
  const upgrade = (bid: string) => app.nav.openSheet('MOD-CONFIRM', {
    title: tr('COOP-BUILD-PLAN-USE', { build: tr(BUILDING_KEY(bid)) }), risky: tr('CL-COOP-BUILD-GO'),
    onRisky: async () => {
      setBusy(true);
      try {
        const r = await app.call('upgradeCoopBuilding', { params: { coopId: id, buildingId: bid as 'c_shop' }, body: {}, idempotencyKey: idem.key() });
        idem.next();
        showToast(tr('COOP-BUILD-UP', { build: tr(BUILDING_KEY(bid)), lvl: r.building.level }), 'success');
        load.reload();
        void reloadMine(app).catch(() => {});
      } catch (e) {
        const code = errCode(e);
        if (code === 'NO_PLANS') showToast(tr('CL-COOP-NO-PLANS'));
        else if (code === 'COOP_LEVEL_TOO_LOW') showToast(tr('CL-COOP-NEED-COOP-LVL', { n: Number((e as ApiCallError).details?.['level'] ?? 0) }));
        else app.handleError(e);
      } finally { setBusy(false); }
    },
  });
  return (
    <>
      <ScreenTitle>{tr('COOP-BUILD-TITLE')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-COOP-BUILD">
        <div class="page col">
          {c ? (
            <div class="card col">
              <span>{c.buildPtsToNext === null ? tr('CL-COOP-MAX-LVL') : tr('COOP-BUILD-LEVEL', { lvl: c.level + 1, cur: num(c.buildPts), need: num(c.buildPts + c.buildPtsToNext) })}</span>
              {c.buildPtsToNext !== null ? <div class="progress"><i style={{ width: `${Math.min(100, (c.buildPts / (c.buildPts + c.buildPtsToNext)) * 100)}%` }} /></div> : null}
              <span class="small muted">{tr('COOP-BUILD-PLAN', { n: load.data?.plansAvailable ?? c.plansAvailable })}</span>
            </div>
          ) : null}
          {load.error && !load.data ? <ErrorBanner what={tr('COOP-BUILD-TITLE')} onRetry={load.reload} /> : null}
          {!load.data ? <Skeleton h={64} n={4} /> : (
            <div class="build-grid">
              {load.data.buildings.map((b) => {
                const locked = b.requiredCoopLevel !== null && (c?.level ?? 0) < b.requiredCoopLevel;
                return (
                  <div key={b.buildingId} class="card col" data-testid={`bld-${b.buildingId}`}>
                    <b>{tr(BUILDING_KEY(b.buildingId))}</b>
                    <span class="small muted">{tr('CL-COOP-LVL', { n: b.level })}{locked ? ` · ${tr('CL-COOP-NEED-COOP-LVL', { n: b.requiredCoopLevel! })}` : ''}</span>
                    <span class="small">{Object.entries(b.bonus).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'}</span>
                    {lead && !locked && b.level < 5 ? <button class="btn small outline" data-testid={`bld-up-${b.buildingId}`} disabled={busy || !online.value || !load.data!.plansAvailable} onClick={() => upgrade(b.buildingId)}>{tr('CL-COOP-BUILD-GO')}</button> : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/** Сдача в склад: монеты летят из выбранной валюты к складу (карточка экрана под листом) или к заголовку листа. */
function flyToStorage(cur: 'nuts' | 'scrap', n: number) {
  const icon = document.querySelector(`[data-testid="donate-${cur}"] svg, [data-testid="donate-${cur}"] img`);
  const target = [...document.querySelectorAll('[data-testid="SCR-COOP-STORAGE"] .card, [data-testid="SCR-COOP-BUILD"] .card, [data-testid="MOD-COOP-DONATE"] .sheet-title')]
    .find((el) => { const r = el.getBoundingClientRect(); return r.height > 0 && r.bottom > 0; }) ?? null;
  if (icon) void flyTo(icon, target, icon, Math.max(3, Math.min(8, Math.round(2 + Math.log10(n + 1) * 2))));
}

export function CoopDonateSheet({ entry }: SheetProps) {
  const app = useApp();
  const id = coopBrief.value?.coopId ?? '';
  const [cur, setCur] = useState<'nuts' | 'scrap'>('nuts');
  const [n, setN] = useState(0);
  const [busy, setBusy] = useState(false);
  const [capLeft, setCapLeft] = useState<number | null>(null);
  const [result, setResult] = useState<number | null>(null);
  const idem = useIdem(app);
  const have = cur === 'nuts' ? wallet.value?.nuts ?? 0 : wallet.value?.scrap ?? 0;
  // «Всё лишнее» оставляет цену ближайшего своего улучшения (ux/07 §9); числа гаража — в состоянии гаража.
  const reserve = cur === 'nuts' ? Number(entry.params['reserve'] ?? 0) : 0;
  const ptsPer = cur === 'nuts' ? 1 : 10;
  const pts = n * ptsPer;
  const over = capLeft !== null && pts > capLeft;
  const give = async () => {
    setBusy(true);
    try {
      const r = await app.call('depositCoop', { params: { coopId: id }, body: { currency: cur, amount: n }, idempotencyKey: idem.key() });
      idem.next();
      flyToStorage(cur, n);
      setWallet(r.wallet);
      setCapLeft(r.dailyCapLeft);
      setResult(r.countedPts);
      setN(0);
      app.haptic('success');
      void reloadMine(app).catch(() => {});
    } catch (e) {
      const code = errCode(e);
      if (code === 'STORAGE_FULL') showToast(tr('CL-COOP-STORAGE-FULL'));
      else app.handleError(e);
    } finally { setBusy(false); }
  };
  return (
    <Sheet entry={entry} title={tr('CL-COOP-DONATE')} testId="MOD-COOP-DONATE" actions={[
      { text: tr('CL-COOP-GIVE'), testId: 'donate-go', disabled: busy || !online.value || n <= 0 || n > have, onClick: give },
    ]}>
      <div class="col">
        <div class="chips">{(['nuts', 'scrap'] as const).map((k) => <button key={k} class={`chip ${cur === k ? 'on' : ''}`} data-testid={`donate-${k}`} onClick={() => { setCur(k); setN(0); }}><I n={k} size={16} /> {amount(k, k === 'nuts' ? wallet.value?.nuts ?? 0 : wallet.value?.scrap ?? 0)}</button>)}</div>
        <input class="input" type="number" inputMode="numeric" min={0} max={have} data-testid="donate-n" value={n || ''} onInput={(e) => setN(Math.max(0, Math.min(have, Math.floor(Number((e.target as HTMLInputElement).value) || 0))))} />
        <div class="row" style={{ gap: 6 }}>
          <button class="chip" onClick={() => setN(Math.min(have, n + 100))}>+100</button>
          <button class="chip" onClick={() => setN(Math.min(have, n + 1000))}>+1 000</button>
          <button class="chip" data-testid="donate-spare" onClick={() => setN(spareAmount(have, reserve))}>{tr('CL-COOP-SPARE')}</button>
        </div>
        {reserve > 0 && cur === 'nuts' ? <span class="small muted">{tr('CL-COOP-SPARE-NOTE', { n: num(reserve) })}</span> : null}
        <span class="small">{tr('CL-COOP-DONATE-PTS', { n: num(pts) })}</span>
        {capLeft !== null ? <span class="small" data-testid="donate-cap">{capLeft > 0 ? tr('CL-COOP-CAP-LEFT', { n: num(capLeft) }) : tr('CL-COOP-CAP-DONE')}</span> : null}
        {over ? <span class="small warn-text" data-testid="donate-over">{tr('CL-COOP-OVER-CAP')}</span> : null}
        {result !== null ? <div class="banner pos" data-testid="donate-result">{tr('CL-COOP-COUNTED', { n: num(result) })}</div> : null}
        <span class="small muted">{tr('COOP-STORE-NOTE')}</span>
      </div>
    </Sheet>
  );
}

// ---------- §10 SCR-COOP-STORAGE ----------
export function CoopStorageScreen(_p: ScreenProps) {
  const app = useApp();
  const id = coopBrief.value?.coopId ?? '';
  const load = useLoad(() => app.call('getCoopStorage', { params: { coopId: id } }));
  const [mine, setMine] = useState(false);
  const me = player.value?.publicId;
  useActionBar({ back: true, main: { text: tr('CL-COOP-DONATE'), testId: 'storage-donate', disabled: !online.value, onClick: () => app.nav.openSheet('MOD-COOP-DONATE') } });
  const log = (load.data?.log ?? []).filter((l) => !mine || l.publicId === me);
  return (
    <>
      <ScreenTitle>{tr('COOP-STORE')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-COOP-STORAGE">
        <div class="page col">
          {load.error && !load.data ? <ErrorBanner what={tr('COOP-STORE')} onRetry={load.reload} /> : null}
          {!load.data ? <Skeleton h={56} n={4} /> : (
            <>
              <div class="card col">
                <span>{tr('CL-COOP-STORE-PTS', { n: num(load.data.buildPts) })} · {tr('CL-COOP-SUMMONS', { n: load.data.summons })}</span>
                <span class="small muted">{tr('COOP-STORE-NOTE')}</span>
                <span class="small muted">{tr('CL-COOP-STORE-SPEND')}</span>
              </div>
              <div class="chips"><button class={`chip ${mine ? '' : 'on'}`} onClick={() => setMine(false)}>{tr('CL-COOP-LOG-ALL')}</button><button class={`chip ${mine ? 'on' : ''}`} data-testid="log-mine" onClick={() => setMine(true)}>{tr('CL-COOP-LOG-MINE')}</button></div>
              {!log.length ? <div class="empty" data-testid="storage-empty">{tr('CL-COOP-STORE-EMPTY')}</div> : (
                <div class="list col">{log.map((l, i) => (
                  <div key={i} class="list-row"><span style={{ flex: 1 }}>{l.displayName}</span><span class="small">{amount(l.currency, l.amount)} → {tr('CL-COOP-PTS', { n: num(l.countedPts) })}</span></div>
                ))}</div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ---------- §16 MOD-COOP-SHOP ----------
export function CoopShopSheet({ entry }: SheetProps) {
  const app = useApp();
  const id = coopBrief.value?.coopId ?? '';
  const load = useLoad(() => app.call('getCoopShop', { params: { coopId: id } }));
  const [pick, setPick] = useState<Shop['items'][number] | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const idem = useIdem(app);
  const d = load.data;
  const buy = async () => {
    if (!pick) return;
    setBusy(true);
    setNote(null);
    try {
      const r = await app.call('buyCoopShop', { params: { coopId: id }, body: { itemId: pick.itemId, qty: 1 }, idempotencyKey: idem.key() });
      idem.next();
      setWallet(r.wallet);
      app.haptic('success');
      app.nav.popSheet();
      app.nav.openSheet('MOD-REWARD', { grant: r.grants });
    } catch (e) {
      const code = errCode(e);
      if (code === 'WEEKLY_LIMIT' || code === 'LIMIT_REACHED') setNote(tr('CL-COOP-SHOP-WEEKLY'));
      else if (code === 'NOT_ENOUGH_MARKS') setNote(tr('CL-COOP-SHOP-NO-MARKS', { n: pick.priceMarks - (d?.marks ?? 0) }));
      else app.handleError(e);
      load.reload();
    } finally { setBusy(false); }
  };
  const short = pick && d ? pick.priceMarks - d.marks : 0;
  const actions = pick ? [
    short > 0
      ? { text: tr('CL-COOP-SHOP-NO-MARKS', { n: short }), disabled: true, onClick: () => {} }
      : { text: tr('CL-COOP-SHOP-BUY', { n: pick.priceMarks }), testId: 'shop-buy', disabled: busy || !online.value || pick.limit?.left === 0, onClick: buy },
    { text: tr('CL-COOP-CANCEL'), tone: 'outline' as const, onClick: () => setPick(null) },
  ] : [];
  return (
    <Sheet entry={entry} title={tr('COOP-LAVKA')} actions={actions} testId="MOD-COOP-SHOP">
      {load.error && !d ? <ErrorBanner what={tr('COOP-LAVKA')} onRetry={load.reload} /> : !d ? <Skeleton h={64} n={2} /> : (
        <div class="col">
          <span data-testid="shop-marks">{tr('CL-COOP-MARKS', { n: d.marks, cap: d.marksCap })}</span>
          {d.marks >= d.marksCap ? <div class="banner warn">{tr('CL-COOP-MARKS-CAP', { cap: d.marksCap })}</div> : null}
          <span class="small muted">{tr('COOP-LAVKA-NOTE')}</span>
          <div class="store-grid">
            {d.items.map((it) => {
              const out = it.limit?.left === 0;
              return (
                <button key={it.itemId} class={`card store-tile ${pick?.itemId === it.itemId ? 'selected' : ''}`} data-testid={`shop-${it.itemId}`} disabled={out} onClick={() => setPick(it)}>
                  <span>{Object.entries(it.grant.currencies ?? {}).map(([k, v]) => amount(k, v)).join(', ') || it.itemId}</span>
                  <b>{tr('CL-COOP-PRICE-MARKS', { n: it.priceMarks })}</b>
                  {it.limit ? <span class="small muted">{out ? tr('CL-COOP-SHOP-MONDAY') : tr('CL-COOP-SHOP-LIMIT', { k: it.limit.total - it.limit.left, limit: it.limit.total })}</span> : null}
                </button>
              );
            })}
          </div>
          {note ? <span class="small danger-text" data-testid="shop-note">{note}</span> : null}
          {short > 0 ? <button class="link-btn small" onClick={() => { app.nav.popSheet(); app.nav.openSheet('MOD-COOP-DONATE'); }}>{tr('CL-COOP-DONATE')}</button> : null}
          <span class="small muted">{tr('CL-COOP-MARKS-PERSONAL')}</span>
        </div>
      )}
    </Sheet>
  );
}
